<template>
  <div>
    <h1>Assessments</h1>
    <p class="text-muted font-weight-bold">
      Create, manage, and analyze student assessments
    </p>
    <hr />

    <b-row class="mb-2">
      <b-col md="8">
        <b-form-group class="mb-0 w-100">
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search assessments..."
            />
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col md="4">
        <v-select
          class="bg-white"
          v-model="subID"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="subjects"
          :reduce="(opt) => opt.id"
          label="name"
          :clearable="false"
          :disabled="subLoading"
          placeholder="Select subject"
        />
      </b-col>
    </b-row>

    <div class="assessment-tabs">
      <b-tabs content-class="mt-2">
        <!-- Completed Tab -->
        <b-tab active>
          <template #title>
            <feather-icon icon="CheckSquareIcon" size="20" />
            <span class="ml-50">Completed</span>
          </template>

          <div
            v-for="(item, index) in completedAssessments"
            :key="index"
            class="mb-2"
          >
            <b-card no-body class="shadow-sm cursor-pointer hover-card">
              <b-card-body class="p-2">
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap"
                >
                  <!-- Left Section: Icon & Info -->
                  <div
                    class="d-flex align-items-center flex-grow-1 mb-1 mb-md-0"
                  >
                    <div
                      class="icon-item rounded mr-2 d-flex align-items-center justify-content-center bg-light text-dark"
                    >
                      <feather-icon icon="FileTextIcon" size="24" />
                    </div>

                    <div>
                      <div class="d-flex align-items-center flex-wrap mb-50">
                        <h5 class="font-weight-bolder text-dark mb-0 mr-1">
                          {{ item.title }}
                        </h5>
                        <b-badge
                          :variant="getBadgeVariant(item.type)"
                          pill
                          class="text-capitalize small px-2"
                        >
                          {{ item.type }}
                        </b-badge>
                      </div>
                      <div>{{ item.subject }} • {{ item.date }}</div>
                    </div>
                  </div>

                  <!-- Right Section: Stats & Action -->
                  <div
                    class="d-flex align-items-center justify-content-end stats-containter"
                  >
                    <div class="text-center mr-3 mr-md-4">
                      <h1 class="font-weight-bolder text-dark mb-0">
                        {{ item.avgScore }}%
                      </h1>
                      <small>Avg Score</small>
                    </div>
                    <div class="text-center mr-3 mr-md-4">
                      <h1 class="font-weight-bolder text-dark mb-0">
                        {{ item.completed }}%
                      </h1>
                      <small>Completed</small>
                    </div>
                    <b-button class="btn-icon mr-3" variant="flat-primary">
                      <feather-icon icon="BarChartIcon" size="20" />
                    </b-button>
                    <b-button class="btn-icon" variant="flat-secondary">
                      <feather-icon icon="ChevronRightIcon" size="20" />
                    </b-button>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-tab>

        <!-- Upcoming Tab -->
        <b-tab>
          <template #title>
            <feather-icon icon="ClockIcon" size="20" />
            <span class="ml-50">Upcoming</span>
          </template>

          <div
            v-for="(item, index) in upcomingAssessments"
            :key="index"
            class="mb-2"
          >
            <b-card no-body class="shadow-sm cursor-pointer hover-card">
              <b-card-body class="p-2">
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap"
                >
                  <div class="d-flex align-items-center flex-grow-1">
                    <div
                      class="icon-item rounded mr-2 d-flex align-items-center justify-content-center bg-light text-dark"
                    >
                      <feather-icon icon="ClockIcon" size="24" />
                    </div>
                    <div>
                      <div class="d-flex align-items-center flex-wrap mb-50">
                        <h5 class="font-weight-bolder text-dark mb-0 mr-1">
                          {{ item.title }}
                        </h5>
                        <b-badge
                          :variant="getBadgeVariant(item.type)"
                          pill
                          class="text-capitalize small px-2"
                        >
                          {{ item.type }}
                        </b-badge>
                      </div>
                      <div>{{ item.subject }} • {{ item.date }}</div>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-button variant="outline-secondary" class="btn-icon mr-1">
                      Edit
                    </b-button>
                    <b-button variant="primary" class="btn-icon">
                      Preview
                    </b-button>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-tab>

        <!-- Drafts Tab -->
        <b-tab>
          <template #title>
            <feather-icon icon="CalendarIcon" size="20" />
            <span class="ml-50">Drafts</span>
          </template>
          <div
            v-for="(item, index) in draftAssessments"
            :key="index"
            class="mb-2"
          >
            <b-card no-body class="shadow-sm cursor-pointer hover-card">
              <b-card-body class="p-2">
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap"
                >
                  <div class="d-flex align-items-center flex-grow-1">
                    <div
                      class="icon-item rounded mr-2 d-flex align-items-center justify-content-center bg-light text-dark"
                    >
                      <feather-icon icon="EditIcon" size="24" />
                    </div>
                    <div>
                      <div class="d-flex align-items-center flex-wrap mb-50">
                        <h5 class="font-weight-bolder text-dark mb-0 mr-1">
                          {{ item.title }}
                        </h5>
                        <b-badge
                          variant="light-danger"
                          pill
                          class="text-capitalize small px-2"
                        >
                          Draft
                        </b-badge>
                      </div>
                      <div>Last edited: {{ item.date }}</div>
                    </div>
                  </div>
                  <b-button class="btn-icon" variant="flat-secondary">
                    <feather-icon icon="ChevronRightIcon" size="20" />
                  </b-button>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BAvatar,
  BButton,
  BBadge,
  BTabs,
  BTab,
} from "bootstrap-vue";

import vSelect from "vue-select";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BAvatar,
    BButton,
    BBadge,
    BTabs,
    BTab,
    vSelect,
  },
  data() {
    return {
      searchQuery: "",
      subLoading: false,
      subID: 0,
      subjects: [
        { id: 0, name: "All Subjects" },
        { id: 1, name: "Math" },
        { id: 2, name: "English" },
        { id: 3, name: "Science" },
      ],
      completedAssessments: [
        {
          title: "Fall Benchmark - Math",
          type: "Benchmark",
          subject: "Mathematics",
          date: "Sun, Sep 15, 2024",
          avgScore: 72,
          completed: 96,
        },
        {
          title: "Unit 3 Quiz - Fractions",
          type: "Formative",
          subject: "Mathematics",
          date: "Fri, Nov 8, 2024",
          avgScore: 78,
          completed: 100,
        },
        {
          title: "Reading Comprehension Check",
          type: "Formative",
          subject: "English Language Arts",
          date: "Tue, Nov 12, 2024",
          avgScore: 81,
          completed: 100,
        },
        {
          title: "Mid-Year ELA Assessment",
          type: "Summative",
          subject: "English Language Arts",
          date: "Sun, Dec 15, 2024",
          avgScore: 76,
          completed: 92,
        },
      ],
      upcomingAssessments: [
        {
          title: "Spring Benchmark - Science",
          type: "Benchmark",
          subject: "Science",
          date: "Mon, Mar 10, 2025",
        },
        {
          title: "Unit 4 Test - Geometry",
          type: "Summative",
          subject: "Mathematics",
          date: "Fri, Mar 21, 2025",
        },
      ],
      draftAssessments: [
        {
          title: "Pop Quiz - Vocabulary",
          date: "Today at 10:30 AM",
        },
        {
          title: "End of Year Review",
          date: "Yesterday",
        },
      ],
    };
  },
  methods: {
    getBadgeVariant(type) {
      if (type === "Benchmark") return "light-secondary";
      if (type === "Formative") return "light-info";
      if (type === "Summative") return "light-warning";
      return "light-primary";
    },
  },
};
</script>

<style scoped>
.icon-item {
  width: 48px;
  height: 48px;
}
.hover-card {
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
}
.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
}
</style>
